import { addClass } from '@opten/dom/add-class';
import { hasClass } from '@opten/dom/has-class';
import { removeClass } from '@opten/dom/remove-class';

import { getSelectorElementFromElement } from '@opten/dom/get-selector-element-from-element';
import { addTransitionEndListener } from '@opten/transition/add-transition-end-listener';

import { eventNamespaceFactory } from '@opten/events/event-namespace-factory';

const stringCollapse = 'Collapse';

export interface CollapseOptions {
	componentClass?: string;
	activeClass?: string;
	activeParentClass?: string;
	showClass?: string;
	showClassDesktop?: string;
	desktopWidth?: number;
	collapsingClass?: string;
	parentElement?: HTMLElement;
	transitionDuration?: number;
	animateProp?: 'height' | 'width' | false;
	isHideable?: boolean;
	closeSiblings?: boolean;
	useShowClassDesktop?: boolean;
	dataParentSelector?: string;
	dataTargetSelector?: string;
	dataToggleSelector?: string;
	hrefSelector?: string;
}

export class Collapse {
	// TODO: Uggly naming
	get toggles(): HTMLElement[] {
		return this.triggers;
	}

	constructor(public element: HTMLElement, options?: CollapseOptions) {
		this.options = {
			...this.options,
			...options,
		};

		if (!this.options.parentElement) {
			this.options.parentElement = getSelectorElementFromElement(
				element,
				this.options.dataParentSelector,
				false
			);
		}

		this.init();
	}
	private options: CollapseOptions = {
		activeClass: 'is-active',
		activeParentClass: 'is-active',
		animateProp: 'height',
		collapsingClass: 'collapse--collapsing',
		componentClass: 'collapse',
		showClass: 'collapse--show',
		showClassDesktop: 'collapse--md-show',
		desktopWidth: 1024,
		transitionDuration: 600,
		isHideable: true,
		closeSiblings: true,
		useShowClassDesktop: false,
		dataParentSelector: 'data-parent',
		dataTargetSelector: 'data-target',
		dataToggleSelector: 'data-toggle',
		hrefSelector: 'href',
	};

	private parent: HTMLElement;
	private triggers: HTMLElement[];

	private target: Element;
	private isTransitioning = false; // when true it will prevent click handlers

	// event names
	private eventNamespaceFn = eventNamespaceFactory('opten.collapse');

	static getInstance(
		element: HTMLElement,
		options?: CollapseOptions,
		create = true
	) {
		let data = element[stringCollapse] as Collapse;

		if (!data && create) {
			data = new Collapse(element, options);
		}

		return data;
	}

	isOpen(): boolean {
		if (
			this.options.useShowClassDesktop &&
			window.outerWidth >= this.options.desktopWidth &&
			hasClass(this.element, this.options.showClassDesktop)
		) {
			addClass(this.element, this.options.showClass);
		}
		removeClass(this.element, this.options.showClassDesktop);
		return hasClass(this.element, this.options.showClass);
	}

	toggle() {
		if (this.isOpen()) {
			if (this.options.isHideable) {
				this.hide();
			}
		} else {
			this.show();
		}
	}

	show() {
		if (this.isTransitioning || this.isOpen()) {
			return;
		}

		if (this.options.closeSiblings && this.parent) {
			const selector = `[${this.options.dataParentSelector}="#${this.parent.id}"]`;
			const actives = Array.from(
				this.parent.querySelectorAll(
					`${selector}.${this.options.showClass}, ${selector}.${this.options.collapsingClass}`
				)
			).filter((active) => active !== this.target);

			if (actives && actives.length) {
				const anyTransitioning = actives.filter((active) => {
					const collapseInstance = active[stringCollapse] as Collapse;
					return collapseInstance && collapseInstance.isTransitioning;
				});

				if (anyTransitioning.length) {
					return;
				}

				actives.forEach((active) =>
					Collapse.getInstance(active as HTMLElement).hide()
				);
			}
		}

		this._handleShow(this.element, this.triggers);
	}

	hide() {
		if (this.isTransitioning || !this.isOpen()) {
			return;
		}

		this._handleClose(this.element, this.triggers);
	}

	destroy() {
		const element = this.element;

		if (element[stringCollapse]) {
			element[stringCollapse] = null;

			removeClass(element, this.options.componentClass);
			removeClass(element, this.options.collapsingClass);
			removeClass(element, this.options.showClass);

			if (this.options.animateProp) {
				element.style[this.options.animateProp] = null;
			}
		}
	}

	private init() {
		// todo: only initalize once, like bootstrap does it?
		this.triggers = Array.from(
			document.querySelectorAll(
				`[${this.options.hrefSelector}="#${this.element.id}"], [${this.options.dataTargetSelector}="#${this.element.id}"]`
			)
		);

		// todo: why do we need that?
		const toggles = Array.from(
			document.querySelectorAll(
				`[${this.options.dataToggleSelector}="collapse"]`
			)
		);
		for (const toggle of toggles) {
			const target = getSelectorElementFromElement(
				toggle,
				this.options.dataTargetSelector
			);
			if (target !== null && target === this.element) {
				this.target = target;
				this.triggers.push(toggle as HTMLElement);
			}
		}

		this.parent = this.options.parentElement;

		// init
		if (!(stringCollapse in this.element)) {
			// prevent adding event handlers twice
			this.triggers.forEach((trigger) =>
				trigger.addEventListener('click', this.handleClick, false)
			);
		}

		if (this.parent) {
			Array.from(
				this.parent.querySelectorAll(
					`[${this.options.dataToggleSelector}="collapse"][${this.options.dataParentSelector}="#${this.parent.id}"]`
				)
			).forEach((element) => {
				this._addAriaAndCollapsedClass(
					getSelectorElementFromElement(
						element,
						this.options.dataTargetSelector
					),
					[element]
				);
			});
		} else {
			this._addAriaAndCollapsedClass(this.element, this.triggers);
		}

		addClass(this.element, this.options.componentClass);

		this.element[stringCollapse] = this;
	}

	private handleClick = (event: Event) => {
		// preventDefault only for <a> elements (which change the URL) not inside the collapsible element
		const trigger = event && (event.currentTarget as HTMLElement);
		if (trigger && trigger.tagName === 'A') {
			event.preventDefault();
		}

		const target = getSelectorElementFromElement(
			trigger,
			this.options.dataTargetSelector
		);
		let data = target[stringCollapse] as Collapse;

		if (!data) {
			data = trigger[stringCollapse] as Collapse;
		}

		if (target) {
			Collapse.getInstance(target, data.options).toggle();
		}
	};

	private _handleShow(collapseElement: HTMLElement, triggers?: Element[]) {
		this.eventNamespaceFn(collapseElement, 'show');

		removeClass(collapseElement, this.options.componentClass);
		addClass(collapseElement, this.options.collapsingClass);
		if (this.options.animateProp) {
			collapseElement.style[this.options.animateProp] =
				collapseElement.scrollHeight + 'px';
		}

		if (triggers && triggers.length) {
			triggers.forEach((trigger) => {
				addClass(trigger, this.options.activeClass);
				trigger.setAttribute('aria-expanded', 'true');
			});
		}

		if (this.parent) {
			addClass(this.parent, this.options.activeClass);
		}

		this.isTransitioning = true;

		addTransitionEndListener(
			collapseElement,
			() => {
				removeClass(collapseElement, this.options.collapsingClass);
				addClass(collapseElement, this.options.componentClass);
				addClass(collapseElement, this.options.showClass);

				this.isTransitioning = false;
				if (this.options.animateProp) {
					collapseElement.style[this.options.animateProp] = null;
				}

				this.eventNamespaceFn(collapseElement, 'shown');
			},
			this.options.transitionDuration
		);
	}

	private _handleClose(collapseElement: HTMLElement, triggers?: Element[]) {
		this.eventNamespaceFn(collapseElement, 'hide');

		if (this.options.animateProp) {
			collapseElement.style[this.options.animateProp] =
				collapseElement.scrollHeight + 'px';
		}

		addClass(collapseElement, this.options.collapsingClass);
		removeClass(collapseElement, this.options.componentClass);
		removeClass(collapseElement, this.options.showClass);
		// force reflow to enable transition
		collapseElement.offsetWidth; // tslint:disable-line

		if (triggers && triggers.length) {
			triggers.forEach((trigger) => {
				const target = getSelectorElementFromElement(
					trigger,
					this.options.dataTargetSelector
				);

				if (target !== null) {
					if (!hasClass(target, this.options.showClass)) {
						removeClass(trigger, this.options.activeClass);
						trigger.setAttribute('aria-expanded', 'false');
					}
				}
			});
		}

		if (this.parent) {
			const selector = `[${this.options.dataParentSelector}="#${this.parent.id}"]`;
			const actives = Array.from(
				this.parent.querySelectorAll(
					`${selector}.${this.options.showClass}, ${selector}.${this.options.collapsingClass}`
				)
			).filter((o) => o !== collapseElement);

			if (this.parent && actives.length === 0) {
				removeClass(this.parent, this.options.activeParentClass);
			}
		}

		this.isTransitioning = true;
		if (this.options.animateProp) {
			collapseElement.style[this.options.animateProp] = null;
		}

		addTransitionEndListener(
			collapseElement,
			() => {
				this.isTransitioning = false;

				removeClass(collapseElement, this.options.collapsingClass);
				addClass(collapseElement, this.options.componentClass);

				if (this.options.animateProp) {
					collapseElement.style[this.options.animateProp] = null;
				}

				this.eventNamespaceFn(collapseElement, 'hidden');
			},
			this.options.transitionDuration
		);
	}

	private _addAriaAndCollapsedClass(element: Element, triggers: Element[]) {
		if (element) {
			const isOpen = hasClass(element, this.options.showClass);

			if (triggers && triggers.length) {
				triggers.forEach((trigger) => {
					isOpen
						? addClass(trigger, this.options.activeClass)
						: removeClass(trigger, this.options.activeClass);

					trigger.setAttribute(
						'aria-expanded',
						isOpen.toString().toLowerCase()
					);
				});
			}
		}
	}
}

export function collapse(
	element: HTMLElement,
	options?: CollapseOptions
): Collapse {
	console.error(
		'collapse deprecated: use new Collapse(element, options) instead. (initialized elements can be accessed via Collapse.getInstance()'
	);
	let data = element[stringCollapse] as Collapse;

	if (!data) {
		data = new Collapse(element, options);
	}

	return data;
}
